import PropTypes from 'prop-types';
import { useRef } from 'react';
import cls from 'classnames';

import useSize from '@/utils/hooks/useSize';

import { analytics, getClickCtaDetail } from '@/utils/analytics';
import { isDDEnv, isWHEnv } from '@/utils/helper';

import CtfCta from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';

import { CtfCtaStyle, CtfWidthOptions } from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

function StoryFeaturedAsset({
  compactSpace,
  assets = [],
  entries = [],
  fields = {},
}) {
  const storyRef = useRef(null);
  const size = useSize(storyRef);
  // @TODO: Improve variable spreading and type checking.
  const {
    title,
    text,
    callToAction,
    superText,
    headingWeight,
    dynamicAsset,
    width,
    hasBackground,
    hideText,
    cloudinaryAssets,
    removeYPadding,
    hasRoundedAsset,
    mobileCloudinaryAsset,
    backgroundColor,
  } = fields;
  const isFullWidth = width === CtfWidthOptions.FULL_WIDTH && !compactSpace;
  const isFullPadding = width === CtfWidthOptions.PADDING;
  const HeadingTag = headingWeight ? headingWeight.toLowerCase() : 'h3';
  const containerClass = `${
    !isFullWidth && !hasBackground && 'container mx-auto'
  } ${width === CtfWidthOptions.NO_LEFT && 'pl-0 no-side'} ${
    width === CtfWidthOptions.NO_RIGHT && 'pr-0 no-side'
  }`;

  const mobileAsset = mobileCloudinaryAsset ?? cloudinaryAssets;
  const isDD = isDDEnv();
  const isWH = isWHEnv();

  return (
    <div className={`story-featured-asset-wrapper relative pb-8 md:pb-12 bg-${backgroundColor}`}>
      <div
        className={`aaco-story aaco-story-featured-asset relative flex flex-col md:flex-row ${
          !compactSpace && !removeYPadding && 'mb-20'
        } justify-center ${containerClass}
          ${
            hasBackground
              ? 'dd-en:bg-progressive-blue dd-kr:bg-progressive-blue'
              : ''
          }
          ${
            isFullPadding &&
            hasBackground &&
            'dd-en:rounded-[8px] dd-kr:mx-[30px]'
          }`}
        aria-label={title}
      >
        <div className="aaco-story-wrap-divide w-full h-full">
          <div
            className={cls('aaco-header__image--mobile md:hidden', {
              'h-full': isDD,
              'h-[248px] md:h-full': isWH,
            })}
          >
            <DynamicAsset
              assets={assets}
              callToAction={callToAction}
              entries={entries}
              title={mobileAsset && title}
              field={mobileAsset ?? dynamicAsset}
              size={size}
              lightbox
              style={
                !isFullWidth
                  ? `${
                      !hasBackground
                        ? 'dd-en:rounded-[8px] dd-kr:rounded-[8px]'
                        : 'dd-en:rounded-t-[8px] dd-kr:rounded-t-[8px]'
                    } dd-en:overflow-hidden dd-kr:overflow-hidden ${
                      hasRoundedAsset
                        ? ''
                        : 'dd-en:md:rounded-none dd-kr:md:rounded-none'
                    }`
                  : ''
              }
              container={isFullPadding ? 'container' : 'full'}
            />
          </div>
          <div className="aaco-header__image--desktop hidden md:block">
            <DynamicAsset
              assets={assets}
              callToAction={callToAction}
              entries={entries}
              title={cloudinaryAssets && title}
              field={cloudinaryAssets ?? dynamicAsset}
              size={size}
              lightbox
              style={
                !isFullWidth
                  ? `${
                      !hasBackground
                        ? 'dd-en:rounded-[8px] dd-kr:rounded-[8px]'
                        : 'dd-en:rounded-t-[8px] dd-kr:rounded-t-[8px]'
                    } dd-en:overflow-hidden dd-kr:overflow-hidden ${
                      hasRoundedAsset
                        ? ''
                        : 'dd-en:md:rounded-none dd-kr:md:rounded-none'
                    }`
                  : ''
              }
              container={isFullPadding ? 'container' : 'full'}
            />
          </div>
        </div>
        {!hideText && (
          <>
            <div
              className={`${
                compactSpace
                  ? ''
                  : 'md:absolute md:top-0 md:right-0 md:bottom-0 md:left-0 md:z-10 md:h-full md:w-full'
              } pointer-events-none ${containerClass}`}
            >
              <div className="aaco-header__bg-shadow w-full h-full" />
            </div>
            <div className={
              cls({
                'aaco-story__content': true,
                'dd-en:mt-[25px] dd-kr:mt-[25px] dd-en:mb-[30px] dd-kr:mb-[30px] mt-6': true,
                'text-center dd-en:text-left dd-kr:text-left dd-en:md:text-center dd-kr:md:text-center': true,
                'dd-en:px-[30px] dd-kr:px-[30px]': !isFullWidth && hasBackground,
                'dd-en:px-[30px] dd-kr:px-[30px]': isFullWidth,
                'md:mt-0 md:h-max md:absolute md:w-1/2 md:bottom-[42px] dd-en:md:bottom-[30px] dd-kr:md:bottom-[30px] md:z-20': !compactSpace,
                'md:!w-full md:!h-full mt-2 md:px-8 !text-left': isWH,
              })
            }>
              {superText && (
                <h6
                  className={`mb-5 text-tag uppercase ${
                    hasBackground
                      ? 'dd-en:80-white-on-dark-blue dd-kr:80-white-on-dark-blue'
                      : 'dd-en:text-grey-6 dd-kr:text-grey-6'
                  } ${isWH && 'text-black'} md:text-white`}
                >
                  {superText}
                </h6>
              )}
              {title && (
                <HeadingTag className={
                  cls(TEXT_BASE_CLASS.h4, {
                    'mb-5 dd-en:mb-[15px] dd-kr:mb-[15px] md:text-white': true,
                    'dd-en:text-white dd-kr:text-white': hasBackground,
                    'dd-en:text-progressive-blue dd-kr:text-progressive-blue dd-en:md:text-white dd-kr:md:text-white': !hasBackground,
                    'md:border-b-2 md:pb-5 md:ml-[90px] md:mt-[100px] md:pr-[100px] md:max-w-[65rem] leading-[39px]': isWH,
                  })
                }>
                  {title}
                </HeadingTag>
              )}
              <div className={
                cls('aaco-story__inner-content', {
                  'md:absolute md:bottom-14 md:left-[110px] dd-kr:md:max-w-[50vw] dd-en:max-w-[50vw] border-l-1 border-l-black md:border-l-white pl-4 dd-kr:border-none dd-en:border-none md:max-w-[450px] uppercase leading-[13px]': isWH,
                })
              }>
                {text && !text.content && (
                  <p
                    className={`text-body ${
                      hasBackground
                        ? 'dd-en:80-white-on-dark-blue dd-kr:80-white-on-dark-blue'
                        : 'dd-en:text-grey-6 dd-kr:text-grey-6'
                    } ${isWH && 'text-black text-[11px]'} md:text-white`}
                  >
                    {text}
                  </p>
                )}
                {text &&
                  text.content &&
                  text.content.length > 0 &&
                  text.content.map(({ nodeType, content }, index) => (
                    <RichText
                      entries={entries}
                      nodeType={nodeType}
                      content={content}
                      key={index}
                      richTextStyle={{
                        paragraph: {
                          customFont: `${TEXT_BASE_CLASS['body']} ${isWH && 'text-[11px]'} md:text-white ${
                            hasBackground
                              ? 'dd-en:text-80-white-on-dark-blue dd-kr:text-80-white-on-dark-blue'
                              : 'dd-en:text-grey-6 dd-kr:text-grey-6'
                          }`,
                          blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                        },
                      }}
                    />
                  ))}
                {(callToAction && isDD) && (
                  <div className="aaco-story__cta dd-en:mt-5 dd-kr:mt-5 md:text-white">
                    <CtfCta
                      type={isDD ? CtfCtaStyle.DD_SECONDARY : CtfCtaStyle.PRIMARY}
                      field={callToAction}
                      entries={entries}
                      invert={hasBackground}
                      onClick={(e, { localUrl, url, name }) =>
                        analytics({
                          window,
                          document,
                          detail: getClickCtaDetail(e, { localUrl, url, name }),
                        })
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// @TODO: define real props for the Story based on it requirements
StoryFeaturedAsset.propTypes = {
  assets: PropTypes.array,
  compactSpace: PropTypes.bool,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default StoryFeaturedAsset;
